/**
 * Normalizes locale codes from two-letter format to BCP-47 format.
 * This ensures consistency when receiving data from external APIs that may
 * return two-letter codes (e.g., "en") instead of BCP-47 format (e.g., "en-US").
 *
 * @param localeCode - The locale code to normalize (e.g., "en", "en-US", "es", "es-ES")
 * @returns The normalized BCP-47 locale code
 */
export const normalizeLocale = (localeCode: string | undefined | null): string => {
  if (!localeCode) {
    return 'en-US'; // Default fallback
  }

  // If already in BCP-47 format (contains hyphen), return as-is
  if (localeCode.includes('-')) {
    return localeCode;
  }
  if (localeCode.includes('_')) {
    return localeCode.replace('_', '-');
  }

  // Map common two-letter codes to their default BCP-47 equivalents
  const localeMap: Record<string, string> = {
    en: 'en-US',
    es: 'es-ES',
    fr: 'fr-FR',
    de: 'de-DE',
    it: 'it-IT',
    pt: 'pt-PT',
    nl: 'nl-NL',
    pl: 'pl-PL',
    ru: 'ru-RU',
    ja: 'ja-JP',
    ko: 'ko-KR',
    zh: 'zh-CN',
    ar: 'ar-SA',
    he: 'he-IL',
    hi: 'hi-IN',
    th: 'th-TH',
    vi: 'vi-VN',
    tr: 'tr-TR',
    el: 'el-GR',
    ro: 'ro-RO',
    uk: 'uk-UA',
    cs: 'cs-CZ',
    sv: 'sv-SE',
    da: 'da-DK',
    fi: 'fi-FI',
    no: 'no-NO',
    hu: 'hu-HU',
    bg: 'bg-BG',
    hr: 'hr-HR',
    sk: 'sk-SK',
    sl: 'sl-SI',
    et: 'et-EE',
    lt: 'lt-LT',
    lv: 'lv-LV',
    mt: 'mt-MT',
    ga: 'ga-IE',
    cy: 'cy-GB',
    // Languages that don't typically have region variants
    am: 'am-ET',
    bn: 'bn-BD',
    gu: 'gu-IN',
    pa: 'pa-IN',
    ta: 'ta-IN',
    fa: 'fa-IR',
    ur: 'ur-PK',
    ps: 'ps-AF',
    km: 'km-KH',
    my: 'my-MM',
    so: 'so-SO',
    sw: 'sw-KE',
    ht: 'ht-HT',
    ne: 'ne-NP',
    tl: 'tl-PH',
    hy: 'hy-AM',
    om: 'om-ET',
    // the following languages do not have region variants and are left as is
    // yi
    // hmn
    // chk
  };

  return localeMap[localeCode.toLowerCase()] || localeCode;
};

/**
 * Normalizes an array of locale codes to BCP-47 format
 */
export const normalizeLocales = (localeCodes: string[] | undefined | null): string[] => {
  if (!localeCodes || !Array.isArray(localeCodes)) {
    return [];
  }
  return localeCodes.map(normalizeLocale);
};

/**
 * Converts BCP-47 locale codes back to two-letter format for backend API compatibility.
 * The backend expects two-letter codes (e.g., "en", "es") rather than BCP-47 format (e.g., "en-US", "es-ES").
 *
 * @param localeCode - The BCP-47 locale code to convert (e.g., "en-US", "en-GB", "es-ES")
 * @returns The two-letter locale code (e.g., "en", "es")
 */
export const denormalizeLocaleForBackend = (localeCode: string | undefined | null): string => {
  if (!localeCode) {
    return 'en'; // Default fallback for backend
  }

  if (localeCode.startsWith('zh-')) {
    return localeCode; // keep as is for Chinese
  }

  // If already in two-letter format (no hyphen), return as-is
  if (!localeCode.includes('-')) {
    return localeCode.toLowerCase();
  }

  // Extract the language part (before the hyphen)
  const languagePart = localeCode.split('-')[0].toLowerCase();
  return languagePart;
};
